/* eslint-disable jsx-a11y/anchor-is-valid */
import { Fragment, useContext, useEffect, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import i18n, { formatNumber } from '@/utils/i18n';
import { OrderFulfillmentDetailFragment } from '../../../../graphql/fragments/orderFulfillmentDetail.generated';
import {
  FaChevronCircleDown,
  FaChevronCircleUp,
  FaImages,
  FaLock,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import { DEFAULT_CONTAINER_TYPE_ICON, getImageUrl } from '@/utils/imageUrl';
import {
  IsVytalEmployee,
  ViewerContext,
} from '../../../../providers/ViewerContextProvider';
import {
  OrderFulfillmentType,
  ContainerTypeAmount,
} from '../../../../graphql/generatedTypes';
import { useUpdateOrderFulfillmentMutation } from '../../../../graphql/mutations/updateOrderFulfillment.generated';
import { Communicator } from '../../../../tools/Communication';
import CleanOrDirtyText from '../../../../components/CleanOrDirtyText';
import AddContainerStationModal from './AddContainerStationModal';
import EditContainerStationModal, {
  SelectedManualAmountsInContainerStation,
} from './EditContainerStationModal';
import getContainersGroupedByType from './functions/getContainersGroupedByType';
import OrderfulfillmentPicturesGallery from './OrderfulfillmentPicturesGallery';
import ContainerStationStatusBadge from '../../../../components/ContainerStation/ContainerStationStatusBadge';
import TrackableItemDisplay from '../../../../components/TrackableItem/TrackableItemDisplay';
import { usePackContainerStationAfterUncountedReturnMutation } from '../../../../graphql/mutations/packContainerStationAfterUncountedReturn.generated';
// import { CustomModal } from '../../../../components/CustomModal';
import CountUncountedModal from './CountUncountedModal';
import { ContainerType as SelectorContainerType } from './OrderFulfillmentAddContainerTypes';
import { ContainerStationStatus as ContainerStationStatusEnum } from '../../../../graphql/generatedTypes';
import ClassicLoadingSpinner from '../../../../components/LoadingSpinner/ClassicLoadingSpinner';

const CONTAINER_NAME_ROW_LIMIT = 10;

const ContainerStationStatus = {
  UNCOUNTED: 1,
  COUNTED: 2,
  SEALED: 3,
  EMPTY: 4,
  last: 5,
};

interface ContainerStationsTableProps {
  orderFulfillment: OrderFulfillmentDetailFragment;
  allowDelete?: boolean;
  refetchOrderfulfillment?: () => void;
  loading?: boolean;
}

const ContainerStationsTable = ({
  orderFulfillment,
  allowDelete,
  refetchOrderfulfillment,
  loading = false,
}: ContainerStationsTableProps) => {
  const viewer = useContext(ViewerContext);
  const isInternal = IsVytalEmployee(viewer);
  const [updateOrderFulfillment] = useUpdateOrderFulfillmentMutation();
  const [packingUnits, setPackingUnits] = useState<Record<number, number>>({});
  const [showAllGroupedContainerNames, setShowAllGroupedContainerNames] =
    useState(false);
  const [showAddContainerStation, setShowAddContainerStation] = useState(false);
  const [showEditContainerStation, setShowEditContainerStation] =
    useState(false);
  const [showCSPicturesModal, setShowCSPicturesModal] = useState(false);
  const [
    selectedManualAmountsInContainerStationToEdit,
    setSelectedManualAmountsInContainerStationToEdit,
  ] = useState<SelectedManualAmountsInContainerStation>();
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [containerStationIdToRemove, setContainerStationIdToRemove] = useState<
    string | null
  >();
  const [containerStationIdForPictures, setContainerStationIdForPictures] =
    useState<string | null>();
  const [showCountModal, setShowCountModal] = useState(false);
  const [countModalStationId, setCountModalStationId] = useState<string | null>(
    null,
  );
  const [countModalManualAmounts, setCountModalManualAmounts] = useState<
    ContainerTypeAmount[]
  >([]);
  const [packAfterUncountedReturn, { loading: packLoading }] =
    usePackContainerStationAfterUncountedReturnMutation();
  const [containerTypesForSelector, setContainerTypesForSelector] = useState<
    SelectorContainerType[]
  >([]);
  const [allContainerTypes, setAllContainerTypes] = useState<
    SelectorContainerType[]
  >([]);

  useEffect(() => {
    Communicator.Get('api/3/Container/GetPackagingUnits', {})
      .then((result) => result.json())
      .then((result) => {
        setPackingUnits(result);
      });
  }, []);

  useEffect(() => {
    Communicator.Get('api/3/containers/getAllContainerTypes', {})
      .then((response) => response.json())
      .then((data) => {
        setAllContainerTypes(data);
      });
  }, []);

  function deleteContainerStation(stationId: string) {
    updateOrderFulfillment({
      variables: {
        order_fulfillment_id: orderFulfillment.id,
        container_station_ids: orderFulfillment.container_stations
          .map((cs) => cs.id)
          .filter((id) => id !== stationId),
      },
      refetchQueries: ['OrderFulfillmentInternal'],
    });
  }

  function addContainerStation(stationId: string) {
    updateOrderFulfillment({
      variables: {
        order_fulfillment_id: orderFulfillment.id,
        container_station_ids: [
          ...orderFulfillment.container_stations.map((cs) => cs.id),
          stationId,
        ],
      },
      refetchQueries: ['OrderFulfillmentInternal'],
    });
  }

  const hasNothingToRender = orderFulfillment.container_stations.length === 0;

  const sortedContainerStationsByStatus = [
    ...orderFulfillment.container_stations,
  ].sort((a, b) => {
    const aStatus = a.status;
    const bStatus = b.status;

    const aOrder =
      ContainerStationStatus[aStatus] || ContainerStationStatus.last;
    const bOrder =
      ContainerStationStatus[bStatus] || ContainerStationStatus.last;

    return aOrder - bOrder;
  });

  return (
    <div className="position-relative">
      {loading && (
        <div
          className="position-absolute d-flex align-items-center justify-content-center bg-opacity-75 start-0 top-0 h-100 w-100 bg-white"
          style={{ zIndex: 1050 }}
        >
          <div className="text-center">
            <ClassicLoadingSpinner />
            <div className="mt-2">{i18n.t('Common:loading')}</div>
          </div>
        </div>
      )}
      <div className="d-flex justify-content-end me-2 mb-2">
        <Button
          className="me-2"
          onClick={() => {
            setShowAddContainerStation(true);
          }}
          color="success"
        >
          <FaPlus /> {i18n.t('Common:add')}
        </Button>
      </div>

      {showAddContainerStation && (
        <AddContainerStationModal
          onClose={() => setShowAddContainerStation(false)}
          onSubmit={(stationId) => addContainerStation(stationId)}
        />
      )}

      {/* Adding responsive will break the header stickness! */}
      <Table striped hover bordered>
        <tbody>
          {hasNothingToRender && (
            <tr>
              <td>-</td>
              {orderFulfillment.type.toUpperCase() ===
                OrderFulfillmentType.Return && <td>-</td>}
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              {isInternal && allowDelete && !hasNothingToRender && <td>-</td>}
            </tr>
          )}
          {sortedContainerStationsByStatus.map((station) => {
            const groupedContainersByType = getContainersGroupedByType(
              station.containers,
              station.manual_amounts,
            );

            const groupedContainersByTypeKeys = Object.keys(
              groupedContainersByType,
            );

            const groupedContainersByTypeEntries = Object.entries(
              groupedContainersByType,
            );

            const countOfContainerStation = groupedContainersByTypeEntries
              .map(([t, containers]) => containers.length)
              .reduce((acc, cur) => {
                if (Number(cur)) {
                  acc = acc + Number(cur);
                }
                return acc;
              }, 0);

            const amountInContainerStation = groupedContainersByTypeEntries
              .map(([t, containers]) => containers.map((c) => c.amount))
              .reduce((acc, cur) => {
                if (Number(cur)) {
                  acc = acc + Number(cur);
                }
                return acc;
              }, 0);

            const hasContainers = groupedContainersByTypeEntries.some(
              ([_, containers]) => {
                return containers.length > 0;
              },
            );

            const hasContainerNames = groupedContainersByTypeEntries.some(
              ([_, containers]) => {
                return containers.some((container) => {
                  return (
                    container.container_name.length > 0 &&
                    !container.container_id.startsWith('manual_amount')
                  );
                });
              },
            );

            const isEditable = groupedContainersByTypeEntries.some(
              ([_, containers]) => {
                return containers.some((container) => {
                  return container.isEditable;
                });
              },
            );

            return (
              <tr key={station.id}>
                <td>
                  <TrackableItemDisplay
                    trackableItem={station}
                    showLink={isInternal}
                    showTrackableItemId={false}
                    size={'s'}
                  />
                </td>
                {orderFulfillment.type.toUpperCase() ===
                  OrderFulfillmentType.Return && (
                  <td>{i18n.t('Common:' + station.returned_sealed)}</td>
                )}
                <td>
                  {packLoading && countModalStationId === station.id ? (
                    <ClassicLoadingSpinner />
                  ) : (
                    <>
                      <ContainerStationStatusBadge status={station.status} />
                      {station.status ===
                        ContainerStationStatusEnum.Uncounted && (
                        <Button
                          className="ms-2"
                          color="primary"
                          onClick={() => {
                            setCountModalStationId(station.id);
                            setCountModalManualAmounts([]);
                            const map = new Map<
                              number,
                              SelectorContainerType
                            >();
                            (station.containers || []).forEach((c) => {
                              const id = c.container_type_id;
                              if (!map.has(id)) {
                                map.set(id, {
                                  id,
                                  name: c.container_type_name,
                                  class: 'event_portfolio',
                                  group: '',
                                  iconUrl: null,
                                  imageUrl: c.container_type_image || null,
                                  namesByLanguage: null,
                                  resolvedName: c.container_type_name || '',
                                  sizeHelper: null,
                                  hexCode: '000000',
                                  packingUnit: packingUnits[id] ?? 0,
                                  archived: false,
                                });
                              }
                            });
                            (station.manual_amounts || []).forEach((m) => {
                              const id = m.container_type_id;
                              if (!map.has(id)) {
                                map.set(id, {
                                  id,
                                  name: m.container_type_name,
                                  class: 'event_portfolio',
                                  group: '',
                                  iconUrl: null,
                                  imageUrl: m.container_type_image || null,
                                  namesByLanguage: null,
                                  resolvedName: m.container_type_name || '',
                                  sizeHelper: null,
                                  hexCode: '000000',
                                  packingUnit: packingUnits[id] ?? 0,
                                  archived: false,
                                });
                              }
                            });
                            setContainerTypesForSelector(
                              Array.from(map.values()),
                            );
                            setShowCountModal(true);
                          }}
                        >
                          {i18n.t('Common:count')}
                        </Button>
                      )}
                    </>
                  )}
                </td>
                <td>
                  {station.pictures.length === 0 && '-'}
                  {station.pictures.length > 0 && (
                    <Button
                      onClick={() => {
                        setContainerStationIdForPictures(station.id);
                        setShowCSPicturesModal(true);
                      }}
                    >
                      <FaImages />
                    </Button>
                  )}
                </td>
                <td className="numbers-formatted">
                  {formatNumber(
                    amountInContainerStation || countOfContainerStation,
                  )}
                </td>
                <td>
                  {(groupedContainersByTypeEntries.length === 0 ||
                    !hasContainers) &&
                    '-'}
                  {groupedContainersByTypeEntries.length > 0 &&
                    hasContainers && (
                      <Table striped hover bordered>
                        <thead>
                          <tr>
                            <th className="sticky-table-header">
                              {i18n.t('Common:containerType')}
                            </th>
                            {hasContainerNames && (
                              <th className="sticky-table-header">
                                {i18n.t('Common:containerName')}
                              </th>
                            )}
                            <th className="sticky-table-header">
                              {i18n.t('Common:amount')}
                            </th>
                            <th className="sticky-table-header">
                              {i18n.t('Common:containerStatus')}
                            </th>
                            {isEditable && (
                              <th className="sticky-table-header"></th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {groupedContainersByTypeEntries.map(
                            ([typeId, containers]) => {
                              let manualAmountsCount = 0;
                              const possibleManualAmounts = containers.reduce(
                                (acc, cur) => {
                                  const curAmount = cur.amount;

                                  if (curAmount) {
                                    acc += curAmount;
                                    manualAmountsCount += 1;
                                  }

                                  return acc;
                                },
                                0,
                              );

                              const firstContainer = containers.at(0);
                              const amount =
                                containers.length +
                                possibleManualAmounts -
                                manualAmountsCount;
                              const renderGrouped = containers.length > 1;

                              return (
                                <Fragment key={typeId}>
                                  {containers.length === 0 && (
                                    <>
                                      <tr>
                                        <td>-</td>
                                        {hasContainerNames && <td>-</td>}
                                        <td>-</td>
                                        <td>-</td>
                                        <td>-</td>
                                      </tr>
                                    </>
                                  )}
                                  {renderGrouped && (
                                    <tr>
                                      <td>
                                        <img
                                          style={{
                                            width: '2rem',
                                            height: '2rem',
                                            marginRight: '0.5rem',
                                          }}
                                          src={getImageUrl(
                                            50,
                                            firstContainer?.container_type_image ||
                                              DEFAULT_CONTAINER_TYPE_ICON,
                                          )}
                                          title={
                                            firstContainer?.container_type_name
                                          }
                                          alt={
                                            firstContainer?.container_type_name ||
                                            ''
                                          }
                                        />{' '}
                                        {firstContainer?.container_type_name}
                                      </td>

                                      {hasContainerNames && (
                                        <td>
                                          {containers
                                            .slice(
                                              0,
                                              showAllGroupedContainerNames
                                                ? containers.length
                                                : CONTAINER_NAME_ROW_LIMIT,
                                            )
                                            .map((container) => {
                                              const isManualAmountContainerId =
                                                container.container_id.startsWith(
                                                  'manual_amount',
                                                );

                                              return (
                                                <>
                                                  {isManualAmountContainerId &&
                                                    container.container_name}
                                                  {!isManualAmountContainerId && (
                                                    <Link
                                                      to={`/container/${container.container_id}`}
                                                    >
                                                      {container.container_name}
                                                    </Link>
                                                  )}

                                                  <br />
                                                </>
                                              );
                                            })}

                                          {!showAllGroupedContainerNames &&
                                            containers.length >
                                              CONTAINER_NAME_ROW_LIMIT && (
                                              <>
                                                <a
                                                  href=""
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    setShowAllGroupedContainerNames(
                                                      true,
                                                    );
                                                  }}
                                                >
                                                  <FaChevronCircleDown />{' '}
                                                  {i18n.t('Common:showAll')}
                                                </a>
                                              </>
                                            )}

                                          {showAllGroupedContainerNames &&
                                            containers.length >
                                              CONTAINER_NAME_ROW_LIMIT && (
                                              <>
                                                <a
                                                  href=""
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    setShowAllGroupedContainerNames(
                                                      false,
                                                    );
                                                  }}
                                                >
                                                  <FaChevronCircleUp />{' '}
                                                  {i18n.t('Common:showLess')}
                                                </a>
                                              </>
                                            )}
                                        </td>
                                      )}

                                      <td className="numbers-formatted">
                                        {formatNumber(amount)}
                                      </td>

                                      <td>
                                        {station.returned_sealed ||
                                        firstContainer?.sealed ? (
                                          <CleanOrDirtyText isClean />
                                        ) : (
                                          <CleanOrDirtyText isDirty />
                                        )}
                                      </td>
                                    </tr>
                                  )}

                                  {!renderGrouped &&
                                    containers.map((container) => {
                                      return (
                                        <tr>
                                          <td>
                                            <img
                                              style={{
                                                width: '2rem',
                                                height: '2rem',
                                                marginRight: '0.5rem',
                                              }}
                                              src={getImageUrl(
                                                50,
                                                container.container_type_image ||
                                                  DEFAULT_CONTAINER_TYPE_ICON,
                                              )}
                                              title={
                                                container.container_type_name
                                              }
                                              alt={
                                                container.container_type_name
                                              }
                                            />{' '}
                                            {container.container_type_name}
                                          </td>

                                          {hasContainerNames && (
                                            <td>
                                              <Link
                                                to={`/container/${container.container_id}`}
                                              >
                                                {container.container_name}
                                              </Link>
                                            </td>
                                          )}

                                          <td className="numbers-formatted">
                                            {formatNumber(
                                              container?.amount || 1,
                                            )}{' '}
                                            {groupedContainersByTypeKeys.length ===
                                              1 &&
                                              packingUnits[
                                                container.container_type_id
                                              ] != null &&
                                              '/ ' +
                                                formatNumber(
                                                  packingUnits[
                                                    container.container_type_id
                                                  ],
                                                )}
                                          </td>

                                          <td>
                                            {station.returned_sealed ||
                                            container?.sealed ? (
                                              <CleanOrDirtyText isClean />
                                            ) : (
                                              <CleanOrDirtyText isDirty />
                                            )}
                                          </td>

                                          {isEditable &&
                                            container?.isEditable && (
                                              <td>
                                                <Button
                                                  className="me-2"
                                                  onClick={() => {
                                                    const isSealed =
                                                      station.returned_sealed ||
                                                      container?.sealed;

                                                    const amount = !isSealed
                                                      ? container.amount
                                                      : undefined;
                                                    const amount_sealed =
                                                      isSealed
                                                        ? container.amount
                                                        : undefined;

                                                    setSelectedManualAmountsInContainerStationToEdit(
                                                      {
                                                        order_fulfillment_id:
                                                          orderFulfillment.id,
                                                        container_station_id:
                                                          station.id,
                                                        manual_amount: {
                                                          amount: amount,
                                                          amount_sealed:
                                                            amount_sealed,
                                                          container_type_id: 0,
                                                          old_container_type_id:
                                                            container.container_type_id,
                                                        },
                                                      },
                                                    );
                                                    setShowEditContainerStation(
                                                      true,
                                                    );
                                                  }}
                                                  color="secondary"
                                                  disabled={!isEditable}
                                                >
                                                  <FaLock />
                                                  {' ' + i18n.t('Common:edit')}
                                                </Button>
                                              </td>
                                            )}
                                        </tr>
                                      );
                                    })}
                                </Fragment>
                              );
                            },
                          )}
                        </tbody>
                      </Table>
                    )}
                </td>

                {isInternal && allowDelete && (
                  <td>
                    <div className="float-end">
                      <Button
                        className="me-2"
                        onClick={() => {
                          setContainerStationIdToRemove(station.id);
                          setShowRemoveModal(true);
                        }}
                        color="danger"
                      >
                        <FaTrash /> {i18n.t('Common:delete')}
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>

        <thead>
          {/* This is below tbody on purpose! To have the <th> sticky above the tbody. */}
          <tr>
            <th className="sticky-table-header">{i18n.t('Common:name')}</th>
            {orderFulfillment.type.toUpperCase() ===
              OrderFulfillmentType.Return && (
              <th className="sticky-table-header">
                {i18n.t('OrderFulfillment:returnedSealed')}
              </th>
            )}
            <th className="sticky-table-header">
              {i18n.t('Common:boxStatus')}
            </th>
            <th className="sticky-table-header">{i18n.t('Common:pictures')}</th>
            <th className="sticky-table-header">
              {i18n.t('Common:containerSum')}
            </th>
            <th className="sticky-table-header"></th>
            {isInternal && allowDelete && !hasNothingToRender && (
              <th className="sticky-table-header"></th>
            )}
          </tr>
        </thead>
      </Table>

      {showEditContainerStation &&
        selectedManualAmountsInContainerStationToEdit?.container_station_id && (
          <EditContainerStationModal
            selectedContainerStationManualAmounts={
              selectedManualAmountsInContainerStationToEdit
            }
            packingUnits={packingUnits}
            onClose={() => setShowEditContainerStation(false)}
            onSubmit={() => {
              refetchOrderfulfillment?.();
            }}
          />
        )}

      {showCSPicturesModal && (
        <OrderfulfillmentPicturesGallery
          pictures={
            orderFulfillment.container_stations.find((station) => {
              return station.id === containerStationIdForPictures;
            })?.pictures
          }
          onClose={() => {
            setShowCSPicturesModal(false);
          }}
        />
      )}

      {showCountModal && countModalStationId && (
        <CountUncountedModal
          isOpen={showCountModal}
          setIsOpen={setShowCountModal}
          orderFulfillmentType={
            orderFulfillment.type.toUpperCase() as OrderFulfillmentType
          }
          manualAmounts={countModalManualAmounts}
          setManualAmounts={setCountModalManualAmounts}
          allContainerTypes={allContainerTypes}
          containerTypesForSelector={containerTypesForSelector}
          acceptButtonDisabled={packLoading}
          onAccept={async () => {
            await packAfterUncountedReturn({
              variables: {
                id: countModalStationId,
                manual_amounts: countModalManualAmounts
                  .filter((m) => (m?.container_type_id || 0) > 0)
                  .map((m) => ({
                    container_type_id: m.container_type_id,
                    amount: m.amount,
                  })),
                picture_ids: [],
                sealed: false,
              },
              refetchQueries: ['OrderFulfillmentInternal'],
            });
            setShowCountModal(false);
            setCountModalStationId(null);
            refetchOrderfulfillment?.();
          }}
        />
      )}

      <Modal
        isOpen={showRemoveModal}
        toggle={() => {
          setShowRemoveModal(false);
        }}
      >
        <ModalBody>
          {i18n.t('OrderFulfillment:genericRemoveModalBody')}
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={() => {
              if (containerStationIdToRemove) {
                deleteContainerStation(containerStationIdToRemove);
              }

              setShowRemoveModal(false);
            }}
          >
            {i18n.t('Common:yes')}
          </Button>{' '}
          <Button
            color="secondary"
            onClick={() => {
              setContainerStationIdToRemove(null);
              setShowRemoveModal(false);
            }}
          >
            {i18n.t('Common:no')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ContainerStationsTable;
